import set from "@rill/set";
import { round } from "lodash";
import get from "lodash/get";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEffectiveDefaultInsightsLens } from "../../../constants/default-insights-lens";
import useCompareState from "../../../hooks/useCompareState";
import i18n from "../../../i18n";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import FloatingButtons from "../../../parts/FloatingButtons";
import VelocityExplanation from "../../../parts/modals/velocity-explanation";
import SettingsNav from "../../../parts/settings-nav";
import { useRillContext } from "../../../rill/RillReact/share/base";
import {
  calculateFreeTrialDaysLeft,
  isCoachConverted,
  isCoachFreeTrial,
  isPerformActiveCoach,
} from "../../../utils/user-helpers";
import { SettingsBackHandler } from "../../coaching-portal/settings/components/SettingsBackHandler";
import GeneralPreferences from "./GeneralPreferences";
import { getUserToRequest } from "./manage-coaches/utils/manage-coaches-utils";
import PerformPreferences from "./PerformPreferences";
import { getPreferencesAccess } from "./preferences-access";
import PressPreferences from "./PressPreferences";
import PullPreferences from "./PullPreferences";
import PushPreferences from "./PushPreferences";
import EnablePullCommunityChatModal from "./pull-preferences/EnablePullCommunityChatModal";
import { canManagePushDashboardDefaults } from "./push-preferences/daily-dashboard/access";
import { savePushDashboardDefaults } from "./push-preferences/daily-dashboard/save";

const LIFTING_STANDARDS = {
  squat: 0.23,
  bench: 0.1,
  deadlift: 0.14,
};

function View() {
  const { locals } = useRillContext();
  const { t } = useTranslation("dashboard");

  const {
    activeUser,
    units: activeUserUnits,
    organizationOwner,
    organizationRoles,
  } = locals;
  //the role (owner when there is none), whether the user is NOT able to edit, and whose account the tab shows
  const {
    roleName,
    notAbleToEdit,
    accountOfTheTab: userToBindInformation,
  } = getPreferencesAccess({
    activeUser,
    organizationOwner,
    organizationRoles,
  });
  const isCoach =
    activeUser.permissions?.includes("coach") &&
    !(!organizationOwner && !isCoachConverted(activeUser));
  const isExpiredPull =
    isCoachFreeTrial(activeUser) && calculateFreeTrialDaysLeft(activeUser) < 1;
  //send the user information from current user or organizationOwner
  const userToSendInformation =
    roleName === "Admin" ? organizationOwner._id : activeUser._id;
  const isFe = Boolean(userToBindInformation.pushAccountInfo);
  const startingCalendarDay = activeUser.startingCalendarDay || 0;
  const activeUserRound = Boolean(get(userToBindInformation, "round"));
  const isPressCoach = Boolean(userToBindInformation.pressAccountInfo);
  const isPushCoach = Boolean(userToBindInformation.pushAccountInfo);
  const isPerformCoach = isPerformActiveCoach(userToBindInformation);
  const hideRepMaximums = Boolean(userToBindInformation.hideRepMaximums);
  const intensityMeasure = userToBindInformation.intensityMeasure || "rpe";
  const defaultInsightsLens = getEffectiveDefaultInsightsLens(
    activeUser,
    organizationOwner
  );
  const hideStrengthMetrics = Boolean(
    userToBindInformation.hideStrengthMetrics
  );
  const hideE1rm = Boolean(userToBindInformation.hideE1rm);
  const leaderboard = Boolean(userToBindInformation.leaderboard || false);
  const chatEnabled = Boolean(activeUser.chatEnabled || false);
  const referAsClients = Boolean(activeUser.referAsClients || false);
  const manualMembership = Boolean(
    userToBindInformation.manualMembership || false
  );

  const [, setInitialState, setCurrentState, hasChanged] = useCompareState();

  const getFormSnapshot = useCallback(() => {
    const form = document.getElementById(
      `form-${locals.query.type || "general"}`
    );
    if (!form) return {};
    return Object.fromEntries(new FormData(form));
  }, [locals.query.type]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const snapshot = getFormSnapshot();
      setInitialState(snapshot);
      setCurrentState(snapshot);
    });
    return () => cancelAnimationFrame(id);
  }, [locals.query.type]);

  const markDirty = useCallback(() => {
    requestAnimationFrame(() => setCurrentState(getFormSnapshot()));
  }, [getFormSnapshot]);

  const [hideVelocitySettings, setHideVelocitySettings] = useState(
    Boolean(userToBindInformation.hideVelocitySettings !== false)
  );
  const language = activeUser.language || "en";

  const velocityThresholds = activeUser.velocityThresholds || null;

  const [thresholds, setThresholds] = useState({
    squat: velocityThresholds
      ? velocityThresholds.squat - LIFTING_STANDARDS.squat
      : 0,
    bench: velocityThresholds
      ? velocityThresholds.bench - LIFTING_STANDARDS.bench
      : 0,
    deadlift: velocityThresholds
      ? velocityThresholds.deadlift - LIFTING_STANDARDS.deadlift
      : 0,
  });

  return (
    <main className="settings">
      <SettingsNav classNames={"mobile-hidden"} />
      <section className="preferences-wrapper">
        <h3 className="force-mobile-hidden">{t("preferences.title")}</h3>
        <SettingsBackHandler title={t("preferences.title")} />
        <nav role="tablist">
          <a
            href="/actions/refresh?type=general"
            aria-current={
              !locals.query.type || locals.query.type === "general" || undefined
            }
          >
            {t("preferences.general")}
          </a>
          {!!isCoach && !isExpiredPull && (
            <a
              href="/actions/refresh?type=pull"
              aria-current={locals.query.type === "pull" || undefined}
            >
              {t("preferences.pull")}
            </a>
          )}
          {isPushCoach && !notAbleToEdit && (
            <a
              href="/actions/refresh?type=push"
              aria-current={locals.query.type === "push" || undefined}
            >
              {t("preferences.push")}
            </a>
          )}
          {isPressCoach && !notAbleToEdit && (
            <a
              href="/actions/refresh?type=press"
              aria-current={locals.query.type === "press" || undefined}
            >
              {t("preferences.press")}
            </a>
          )}
          {isPerformCoach && !notAbleToEdit && (
            <a
              href="/actions/refresh?type=perform"
              aria-current={locals.query.type === "perform" || undefined}
            >
              PERFORM
            </a>
          )}
        </nav>

        {locals.query.type === "push" && (
          <PushPreferences
            locals={locals}
            showTab={locals.query.type === "push"}
            userToSendInformation={userToSendInformation}
            onFormChange={markDirty}
          />
        )}
        {(!locals.query.type || locals.query.type === "general") && (
          <GeneralPreferences
            userToSendInformation={userToSendInformation}
            roleName={roleName}
            activeUserUnits={activeUserUnits}
            startingCalendarDay={startingCalendarDay}
            notAbleToEdit={notAbleToEdit}
            activeUserRound={activeUserRound}
            isFe={isFe}
            isCoach={isCoach}
            hideRepMaximums={hideRepMaximums}
            userToBindInformation={userToBindInformation}
            hideStrengthMetrics={hideStrengthMetrics}
            hideE1rm={hideE1rm}
            leaderboard={leaderboard}
            intensityMeasure={intensityMeasure}
            defaultInsightsLens={defaultInsightsLens}
            referAsClients={referAsClients}
            chatEnabled={chatEnabled}
            manualMembership={manualMembership}
            isPressCoach={isPressCoach}
            hideVelocitySettings={hideVelocitySettings}
            setHideVelocitySettings={setHideVelocitySettings}
            thresholds={thresholds}
            setThresholds={setThresholds}
            showTab={!locals.query.type || locals.query.type === "general"}
            isOrganization={Boolean(activeUser.organizationId)}
            language={language}
            onFormChange={markDirty}
          />
        )}
        {locals.query.type === "pull" && (
          <PullPreferences
            isFe={isFe}
            isCoach={isCoach}
            referAsClients={referAsClients}
            chatEnabled={chatEnabled}
            userToSendInformation={userToSendInformation}
            roleName={roleName}
            coachId={userToBindInformation._id}
            zapierApiKey={
              userToBindInformation.zapierApiKey ??
              userToBindInformation.performSettings?.zapierApiKey
            }
            canGenerateKey={
              String(activeUser._id) === String(userToBindInformation._id)
            }
            onFormChange={markDirty}
            inAppCommunityEnabled={
              userToBindInformation.pullAccountInfo?.inAppCommunityEnabled ===
              true
            }
            schedulingEnabled={
              userToBindInformation.pullAccountInfo?.schedulingEnabled === true
            }
          />
        )}
        {locals.query.type === "press" && (
          <PressPreferences
            locals={locals}
            isPressCoach={isPressCoach}
            manualMembership={manualMembership}
            userToSendInformation={userToSendInformation}
            onFormChange={markDirty}
          />
        )}
        {locals.query.type === "perform" && (
          <PerformPreferences
            userToSendInformation={userToSendInformation}
            roleName={roleName}
            dailyPendingDigest={
              userToBindInformation.performSettings?.notificationPreferences
                ?.dailyPendingDigest !== false
            }
            emailOnNewPending={
              userToBindInformation.performSettings?.notificationPreferences
                ?.emailOnNewPending === true
            }
            inAppCommunityEnabled={
              userToBindInformation.performSettings?.inAppCommunityEnabled ===
              true
            }
            schedulingEnabled={
              userToBindInformation.performSettings?.schedulingEnabled === true
            }
            coachId={userToBindInformation._id}
            zapierApiKey={
              userToBindInformation.zapierApiKey ??
              userToBindInformation.performSettings?.zapierApiKey
            }
            canGenerateKey={
              String(activeUser._id) === String(userToBindInformation._id)
            }
            onFormChange={markDirty}
          />
        )}

        <footer>
          <FloatingButtons
            failedText={t("common:discard")}
            successText={t("common:save")}
            successDisabled={!hasChanged}
            handleFailed={() => {
              const form = document.getElementById(
                `form-${locals.query.type || "general"}`
              );
              form?.reset();
              const snapshot = getFormSnapshot();
              setInitialState(snapshot);
              setCurrentState(snapshot);
            }}
            handleSuccess={() => {
              const snapshot = getFormSnapshot();
              setInitialState(snapshot);
              setCurrentState(snapshot);
            }}
            classNames={"mobile-hidden"}
            label={t("preferences.title")}
            submitForm={`form-${locals.query.type || "general"}`}
          />
        </footer>
      </section>
    </main>
  );
}

/**
 * This function is to call it when the setting is for the general, pull and press tav
 * @param {*} body
 * @param {*} locals
 * @param {*} api
 * @param {*} assert
 */
const handlePreferencesPost = async (body, locals, api) => {
  const { activeUser, organizationOwner, search } = locals;
  const isPullPreference = search?.includes("pull");
  //retrive the user information from current user or organizationOwner
  const userToBindInformation =
    body.roleName === "Owner" ? activeUser : organizationOwner;
  const isFe = Boolean(userToBindInformation?.pushAccountInfo);
  const isCoach = activeUser.permissions?.includes("coach");

  // To make sure only coaches with access to the velocity settings get updated
  if (isFe || isCoach) {
    const {
      deadliftThreshold,
      benchThreshold,
      squatThreshold,
      timeMinutesDefault,
      timeSecondsDefault,
    } = body;
    // REST TIMES

    // Null in case the user doesn't update the default (which is null as well) so the time isn't stored as a 0
    const defaultTime = timeSecondsDefault + timeMinutesDefault * 60 || null;

    // Delete the input information from the body in case it causes conflicts with the api
    delete body.timeSecondsDefault;
    delete body.timeMinutesDefault;

    // This loop will get the values from the body and create the reps time object
    // It will also delete the input information from the body
    // the object looks like this:
    // reps = {
    //   rep1: 67,
    //   rep2: null,
    //   ...
    //   rep12: 114
    // }
    const reps = {};
    for (let i = 1; i < 13; i++) {
      const minutes = body[`timeMinutes${i}`];
      const seconds = body[`timeSeconds${i}`];
      reps[`rep${i}`] = seconds + minutes * 60 || null;
      delete body[`timeMinutes${i}`];
      delete body[`timeSeconds${i}`];
    }

    const restTime = {
      default: defaultTime,
      reps,
    };

    body.restTime = restTime;

    if (body.hideVelocitySettings) {
      // Consequences of poor planning for hiding the velocity settings
      // Now we have to reverse the value of the input here or it will destroy everything
      body.hideVelocitySettings = false;

      // VELOCITY
      const velocityThresholds = {
        squat: round(LIFTING_STANDARDS.squat + squatThreshold, 2),
        bench: round(LIFTING_STANDARDS.bench + benchThreshold, 2),
        deadlift: round(LIFTING_STANDARDS.deadlift + deadliftThreshold, 2),
      };

      body.velocityThresholds = velocityThresholds;
    } else {
      body.hideVelocitySettings = true;
    }

    delete body.deadliftThreshold;
    delete body.benchThreshold;
    delete body.squatThreshold;

    if (body.collectRepMax) {
      body.collectRepMax = Object.keys(body.collectRepMax);
    }
  }

  body.action = "updateUserPreferences";
  //user to send the info (current or owner of the organization)
  const userToUpdate = body.userToSendInformation;
  //check if the user is admin or owner
  const isAdmin = body.roleName === "Admin";
  const isOwner = body.roleName === "Owner";
  //delete irrelevant information
  delete body.roleName;
  delete body.userToSendInformation;

  //if chatEnabled was already true and the user saves the preferences without turn off the chat we are going to delete
  //the property for the body to not call needlessly the processCoach method in the msb-chat-script-main project
  if (locals.activeUser.chatEnabled && body.chatEnabled) {
    delete body.chatEnabled;
  }
  //if the user is admin, we are going to send units, chat and startingCalendarDay from admin and the rest of the information would be for owner
  if (isAdmin) {
    const adminBody = {
      units: body.units,
      startingCalendarDay: body.startingCalendarDay,
      language: body.language,
    };
    if (body.chatEnabled !== undefined) {
      adminBody.chatEnabled = body.chatEnabled;
    }
    delete body.units;
    delete body.startingCalendarDay;
    delete body.chatEnabled;
    delete body.language;
    await api(`user/${locals.activeUser._id}`, {
      method: "PUT",
      body: adminBody,
    });
  }
  if ((isAdmin || isOwner) && activeUser.organizationId) {
    const {
      hideVelocitySettings,
      hideStrengthMetrics,
      hideRepMaximums,
      hideE1rm,
      intensityMeasure,
      restTime,
      round,
      preferredLoadInput,
      collectRepMax,
    } = body;

    const organizationBody = {
      ownerId: userToUpdate,
      ...Object.fromEntries(
        Object.entries({
          hideVelocitySettings,
          hideStrengthMetrics,
          hideRepMaximums,
          hideE1rm,
          intensityMeasure,
          restTime,
          round,
          preferredLoadInput,
          collectRepMax,
        }).filter(([_, value]) => value != null)
      ),
    };

    let userBody = { ...body };

    if (isPullPreference) {
      const userToRequest = getUserToRequest(activeUser, organizationOwner);
      const { pullAccountInfo = {} } = userToRequest;
      const inAppCommunityEnabled =
        body.inAppCommunityEnabled === "true" ||
        body.inAppCommunityEnabled === true;
      const wasEnabled = pullAccountInfo?.inAppCommunityEnabled === true;
      if (wasEnabled && !inAppCommunityEnabled) {
        await api("disable-pull-community-chat", {
          method: "POST",
          body: {},
        });
      }
      userBody = {
        ...userBody,
        pullAccountInfo: {
          ...pullAccountInfo,
          inAppCommunityEnabled,
          ...(body.schedulingEnabled !== undefined && {
            schedulingEnabled:
              body.schedulingEnabled === "true" ||
              body.schedulingEnabled === true,
          }),
        },
      };
    }

    try {
      await api(`user/${userToUpdate}`, {
        method: "PUT",
        body: userBody,
      });
      await api(`update-organization-general-preferences`, {
        method: "PUT",
        body: organizationBody,
      });
    } catch (error) {
      console.error("error updating an organization", error);
    }
  } else {
    const userToRequest = getUserToRequest(activeUser, organizationOwner);
    const {
      pressCustomBranding = {},
      pressAccountInfo = {},
      pullAccountInfo = {},
    } = userToRequest;
    let newBody = {
      ...body,
    };

    if (pressAccountInfo && Object.keys(pressAccountInfo).length > 0) {
      newBody = {
        ...newBody,
        pressAccountInfo: {
          ...pressAccountInfo,
          inAppCommunityEnabled: body.inAppCommunityEnabled,
          externalCommunityEnabled: body.externalCommunityEnabled,
          ...(body.schedulingEnabled !== undefined && {
            schedulingEnabled: body.schedulingEnabled,
          }),
        },
        pressCustomBranding: {
          ...pressCustomBranding,
          ...(body.communityLink !== undefined && {
            communityLink: body.communityLink,
          }),
        },
      };
    }

    if (isPullPreference) {
      const inAppCommunityEnabled =
        body.inAppCommunityEnabled === "true" ||
        body.inAppCommunityEnabled === true;
      const wasEnabled = pullAccountInfo?.inAppCommunityEnabled === true;
      if (wasEnabled && !inAppCommunityEnabled) {
        await api("disable-pull-community-chat", {
          method: "POST",
          body: {},
        });
      }
      newBody = {
        ...newBody,
        pullAccountInfo: {
          ...pullAccountInfo,
          inAppCommunityEnabled,
          ...(body.schedulingEnabled !== undefined && {
            schedulingEnabled:
              body.schedulingEnabled === "true" ||
              body.schedulingEnabled === true,
          }),
        },
      };
    }

    await api(`user/${userToUpdate}`, {
      method: "PUT",
      body: newBody,
    });
  }
};

const handleStoreFrontPost = async (body, locals, api, assert) => {
  const { activeUser, organizationOwner } = locals;
  const userToRequest = getUserToRequest(activeUser, organizationOwner);
  const { customBranding = {}, pushAccountInfo = {} } = userToRequest;
  let newBody = {};

  const {
    externalCommunityEnabled = false,
    inAppCommunityEnabled = false,
    communityLink = "",
    global = "",
    paymentSuccess = "",
  } = body;

  newBody.customBranding = { ...customBranding };

  // User has permissions to update the snippets
  if (customBranding?.storefrontSnippets?.active) {
    newBody.customBranding.storefrontSnippets = {
      active: true,
      ...(global && { global }),
      ...(paymentSuccess && { paymentSuccess }),
    };
  } else if (global.length > 0 || paymentSuccess.length > 0) {
    // Throw an error if the user doesn't have permissions to update the snippets and he/she decided to modify it
    assert(false, 400, "Ask for permissions in order to update the scripts");
  }

  //Update the community options
  if (pushAccountInfo && Object.keys(pushAccountInfo).length > 0) {
    newBody = {
      ...newBody,
      pushAccountInfo: {
        ...pushAccountInfo,
        inAppCommunityEnabled,
        externalCommunityEnabled,
      },
      customBranding: {
        ...newBody.customBranding,
        ...(communityLink !== undefined && { communityLink }),
      },
    };
  }

  await api(`user/${userToRequest._id}`, {
    method: "PUT",
    body: newBody,
  });
};

const handlePerformPreferencesPost = async (body, locals, api) => {
  const { activeUser, organizationOwner } = locals;
  const userToBindInformation =
    body.roleName === "Owner" ? activeUser : organizationOwner;
  const userToUpdate = body.userToSendInformation;
  const existingSettings = userToBindInformation.performSettings || {};

  await api(`user/${userToUpdate}`, {
    method: "PUT",
    body: {
      performSettings: {
        ...existingSettings,
        inAppCommunityEnabled: body.inAppCommunityEnabled,
        ...(body.schedulingEnabled !== undefined && {
          schedulingEnabled: body.schedulingEnabled,
        }),
        notificationPreferences: {
          dailyPendingDigest: body.dailyPendingDigest,
          emailOnNewPending: body.emailOnNewPending,
        },
      },
    },
  });
};

const PreferencesController = (app) => {
  app
    .get(
      "/preferences",
      set("heading", "Preferences"),
      async ({ res, req, locals, load }) => {
        const { query } = req;
        const modal = query.modal || {};

        if (query.type === "push" && canManagePushDashboardDefaults(locals)) {
          await load("pushDashboardDefaults");
        }

        locals.modal = {
          "velocity-explanation": <VelocityExplanation />,
          "activity-feed": <ActivityDrawer />,
          "enable-pull-community-chat": (
            <EnablePullCommunityChatModal locals={locals} />
          ),
        }[modal.type];

        res.body = <View />;
      }
    )
    .post("/preferences", async ({ req, locals, res, api, flash, assert }) => {
      const { body } = req;
      const { search } = locals;
      const isPushPreference = search?.includes("push");
      const isPerformPreference = search?.includes("perform");

      try {
        if (isPerformPreference) {
          await handlePerformPreferencesPost(body, locals, api);
        } else if (isPushPreference) {
          await handleStoreFrontPost(body, locals, api, assert);
          await savePushDashboardDefaults(body, locals, api);
        } else {
          await handlePreferencesPost(body, locals, api);
        }
        flash("success", i18n.t("auth:user.updatedSuccess"));
        res.backDontScroll();
      } catch (e) {
        flash("error", e.message);
        res.backDontScroll();
      }
    });
};

export default PreferencesController;
